import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { captureException, captureMessage } from '../tracking/sentry/sentry';

const useStreamingVideo = ({
  src,
  ref,
  hlsConfig,
}: {
  src?: string;
  ref: MutableRefObject<HTMLVideoElement | null>;
  hlsConfig?: Partial<Hls.Config>;
}) => {
  const hlsRef = useRef<Hls>();
  const [doneLoading, setDoneLoading] = useState(false);
  const playPromise = useRef<Promise<void>>();

  useEffect(() => {
    if (!src || !ref?.current) {
      setDoneLoading(true);
      return;
    }

    if (!ref.current?.canPlayType('application/x-mpegURL')) {
      import('hls.js/dist/hls.light.js').then(({ default: Hls }) => {
        const hls = new Hls({ capLevelToPlayerSize: true, ...hlsConfig });

        hlsRef.current = hls;
        ref.current && hls.attachMedia(ref.current);

        hls.on(Hls.Events.MEDIA_ATTACHED, function () {
          hls.loadSource(src);
          hls.on(Hls.Events.MANIFEST_PARSED, function (_, data) {
            hls.startLevel = data.levels.length;
            hls.startLoad();
            setDoneLoading(true);
          });
        });
      });
      return () => hlsRef.current && hlsRef.current.destroy();
    } else {
      setDoneLoading(true);
    }
  }, [src, ref, hlsConfig]);

  const pause = useCallback(async () => {
    try {
      if (playPromise.current !== undefined) {
        await playPromise.current;
        ref.current?.pause();
      } else {
        ref.current?.pause();
      }
    } catch (error) {
      // @ts-expect-error FIXME: handle error types explicitly for improved error handling
      if (error.name === 'NotAllowedError') {
        // Benign error
        // eslint-disable-next-line no-console
        console.log('video autoplay was not allowed');
        return;
      }

      captureException(error, { tags: { errorContext: 'video playback', action: 'pause video' } });
    }
  }, [ref]);

  const play = useCallback(async () => {
    if (!ref.current) {
      captureMessage('Trying to play video but ref is not defined');
      return;
    }
    try {
      playPromise.current = ref.current.play();
      await playPromise.current;
    } catch (error) {
      // @ts-expect-error FIXME: handle error types explicitly for improved error handling
      if (error.name === 'NotAllowedError') {
        // Benign error
        // eslint-disable-next-line no-console
        console.log('video autoplay was not allowed');
        return;
      }

      captureException(error, { tags: { errorContext: 'video playback', action: 'play video' } });
    }
  }, [ref]);

  return {
    doneLoading,
    play,
    pause,
  };
};

export default useStreamingVideo;
